/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";
const GD = "#005885";
const GRN = "#1D9E75";
const CARD = "#FFFFFF";
const B = "#E1E9EE";
const B2 = "#C5D8EA";
const RED = "#D85A30";
const RED2 = "#FEF2F2";
const HONEY = "#F5A623";

const SectionLabel = ({ children }) => (
  <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, letterSpacing: "0.2em", color: "#999999", textTransform: "uppercase", marginBottom: 16 }}>{children}</div>
);
const Divider = () => <div style={{ height: 1, background: B, margin: "28px 0" }} />;

const TONES = [
  { id: "professional", label: "Professional", sample: "This shift in AI regulation signals a broader move toward accountability in tech. For leaders building in this space, the implications are strategic — not just legal." },
  { id: "conversational", label: "Conversational", sample: "Honestly, this AI regulation news has me thinking about how quickly the ground is shifting. Anyone else feel like the rules are being written while we're all still figuring out the game?" },
  { id: "bold", label: "Bold", sample: "Most companies are treating AI regulation as a compliance checkbox. Wrong. This is a competitive moat for anyone paying attention." },
  { id: "educational", label: "Educational", sample: "The new AI regulation framework introduces three key changes that every tech leader should understand: scope expansion, mandatory audits, and cross-border enforcement." },
  { id: "storytelling", label: "Storytelling", sample: "A founder I spoke with last week said something that stuck with me — 'We built the product first and asked permission later.' With these new AI rules, that playbook just expired." },
];

const MBTI_GROUPS = [
  { id: "analysts", icon: "\u{1F9E0}", name: "Analysts", sub: "Strategic, logical, independent", types: [
    { type: "INTJ", label: "Architect", desc: "Strategic and precise — cuts straight to the structural insight" },
    { type: "INTP", label: "Logician", desc: "Intellectually curious — highlights overlooked angles and paradoxes" },
    { type: "ENTJ", label: "Commander", desc: "Commanding and decisive — leads with the strategic takeaway" },
    { type: "ENTP", label: "Debater", desc: "Sharp and provocative — challenges conventional wisdom" },
  ]},
  { id: "diplomats", icon: "\u{1F49A}", name: "Diplomats", sub: "Empathetic, purposeful, inspiring", types: [
    { type: "INFJ", label: "Advocate", desc: "Purpose-driven — connects topics to deeper human truth" },
    { type: "INFP", label: "Mediator", desc: "Warm and idealistic — frames topics through a human lens" },
    { type: "ENFJ", label: "Protagonist", desc: "Charismatic and people-first — ties insight to impact" },
    { type: "ENFP", label: "Campaigner", desc: "Energetic and imaginative — highlights exciting possibilities" },
  ]},
  { id: "sentinels", icon: "\u{1F3DB}", name: "Sentinels", sub: "Reliable, organized, practical", types: [
    { type: "ISTJ", label: "Logistician", desc: "Measured and factual — references specific data and methods" },
    { type: "ISFJ", label: "Defender", desc: "Genuine and helpful — frames topics around serving others" },
    { type: "ESTJ", label: "Executive", desc: "Confident and structured — leads with the actionable insight" },
    { type: "ESFJ", label: "Consul", desc: "Friendly and inclusive — connects to teamwork and values" },
  ]},
  { id: "explorers", icon: "\u{1F30A}", name: "Explorers", sub: "Spontaneous, bold, hands-on", types: [
    { type: "ISTP", label: "Virtuoso", desc: "Concise and pragmatic — focuses on how things work" },
    { type: "ISFP", label: "Adventurer", desc: "Understated and authentic — finds the human angle" },
    { type: "ESTP", label: "Entrepreneur", desc: "Bold and high-energy — gets to the point fast" },
    { type: "ESFP", label: "Entertainer", desc: "Upbeat and relatable — makes topics feel alive" },
  ]},
];

const MBTI_TO_TONE = {
  INTJ: "professional", INTP: "professional", ISTJ: "professional", ESTJ: "professional", ISTP: "professional",
  ENTJ: "bold", ESTP: "bold", ENTP: "bold",
  INFJ: "storytelling", INFP: "storytelling", ISFP: "storytelling", ISFJ: "storytelling",
  ENFJ: "conversational", ENFP: "conversational", ESFJ: "conversational", ESFP: "conversational",
};

const ALL_MBTI = MBTI_GROUPS.flatMap(g => g.types);

export default function ProfileTab({ user, setUser, CATEGORIES }) {
  const [liConnected, setLiConnected] = useState(null);
  const [profileImgError, setProfileImgError] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  // Saved voice state (from DB)
  const savedTone = user?.tone || "professional";
  const savedMbti = user?.mbti_type || null;

  // Voice editor state
  const [voiceEditing, setVoiceEditing] = useState(false);
  const [voiceSaving, setVoiceSaving] = useState(false);
  const [draftTone, setDraftTone] = useState(savedTone);
  const [draftMbti, setDraftMbti] = useState(savedMbti);

  const [topicIds, setTopicIds] = useState(user?.topics || []);
  const [topicSelection, setTopicSelection] = useState(user?.topic_selection || {});
  const [topicWarning, setTopicWarning] = useState(false);
  const [topicsSaving, setTopicsSaving] = useState(false);
  const [topicsSaved, setTopicsSaved] = useState(false);
  const [expandedTopic, setExpandedTopic] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/app/api/auth/linkedin/status?ping=1`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setLiConnected(d.connected === true))
      .catch(() => setLiConnected(false));
  }, []);

  const openVoiceEditor = () => {
    setDraftTone(savedTone);
    setDraftMbti(savedMbti);
    setVoiceEditing(true);
  };

  const cancelVoiceEditor = () => {
    setDraftTone(savedTone);
    setDraftMbti(savedMbti);
    setVoiceEditing(false);
  };

  const selectDraftTone = (toneId) => {
    setDraftTone(toneId);
    setDraftMbti(null);
  };

  const selectDraftMbti = (type) => {
    setDraftMbti(type);
    setDraftTone(MBTI_TO_TONE[type] || "professional");
  };

  const saveVoice = async () => {
    setVoiceSaving(true);
    try {
      if (draftMbti) {
        const derivedTone = MBTI_TO_TONE[draftMbti] || "professional";
        await fetch(`${API_BASE}/app/api/user/settings`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mbti_type: draftMbti, tone: derivedTone }) });
        setUser(u => u ? { ...u, mbti_type: draftMbti, tone: derivedTone } : u);
      } else {
        await fetch(`${API_BASE}/app/api/user/settings`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mbti_type: null, tone: draftTone }) });
        setUser(u => u ? { ...u, mbti_type: null, tone: draftTone } : u);
      }
      setVoiceEditing(false);
    } catch {}
    setVoiceSaving(false);
  };

  const saveTopics = async () => {
    setTopicsSaving(true);
    try {
      await fetch(`${API_BASE}/app/api/user/topics`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ topics: topicIds, topic_selection: topicSelection }) });
      setUser(u => u ? { ...u, topics: topicIds, topic_selection: topicSelection } : u);
      setTopicsSaved(true);
      setTimeout(() => setTopicsSaved(false), 3000);
    } catch {}
    setTopicsSaving(false);
  };

  const toggleTopic = (id) => {
    if (topicIds.includes(id)) {
      setTopicIds(t => t.filter(x => x !== id));
      setTopicWarning(false);
    } else if (topicIds.length >= 5) {
      setTopicWarning(true);
    } else {
      setTopicIds(t => [...t, id]);
      setTopicWarning(false);
    }
  };

  // Preview sample for currently selected draft voice
  const previewSample = (() => {
    if (draftMbti) {
      const info = ALL_MBTI.find(t => t.type === draftMbti);
      const mappedTone = MBTI_TO_TONE[draftMbti] || "professional";
      const toneData = TONES.find(t => t.id === mappedTone) || TONES[0];
      return { label: `${draftMbti} — ${info?.label || ""}`, sample: toneData.sample, desc: info?.desc || "" };
    }
    const toneData = TONES.find(t => t.id === draftTone) || TONES[0];
    return { label: toneData.label, sample: toneData.sample, desc: "" };
  })();

  // Hero display for saved voice
  const heroDisplay = (() => {
    if (savedMbti) {
      const info = ALL_MBTI.find(t => t.type === savedMbti);
      return { title: `${info?.label || savedMbti} (${savedMbti})`, desc: info?.desc || "" };
    }
    const toneData = TONES.find(t => t.id === savedTone) || TONES[0];
    return { title: toneData.label, desc: "" };
  })();

  return (
    <div>
      <SectionLabel>Your profile</SectionLabel>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666666", letterSpacing: "0.1em", marginBottom: 8 }}>NAME</div>
        <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 8, padding: "12px 14px", fontSize: 16, color: "#666666" }}>{user?.linkedin_name || user?.first_name || "\u2014"}</div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666666", letterSpacing: "0.1em", marginBottom: 8 }}>EMAIL · used for login</div>
        <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 8, padding: "12px 14px", fontSize: 16, color: "#666666" }}>
          {user?.email?.startsWith("pending-") ? "Not set" : (user?.email || "\u2014")}
        </div>
      </div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666666", letterSpacing: "0.1em", marginBottom: 8 }}>PROFILE IMAGE</div>
        {user?.linkedin_profile_image && !profileImgError ? (
          <img src={`${API_BASE}/app/api/user/avatar?user_id=${user.id}`} alt={user.first_name || "You"} style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: `2px solid ${B}` }} onError={() => setProfileImgError(true)} />
        ) : (
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: G, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 28, color: "#fff", fontWeight: 600 }}>{(user?.linkedin_name || user?.first_name || "?")[0].toUpperCase()}</span>
          </div>
        )}
        <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#999", marginTop: 8 }}>Pulled from LinkedIn — reconnect to refresh.</div>
      </div>

      <Divider />
      <SectionLabel>Your voice</SectionLabel>

      {/* Collapsed hero state */}
      {!voiceEditing && (
        <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "18px 20px", marginBottom: 20 }}>
          <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#999", marginBottom: 6 }}>Your posts sound:</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#1A1A1A", marginBottom: heroDisplay.desc ? 4 : 12 }}>{heroDisplay.title}</div>
          {heroDisplay.desc && <div style={{ fontSize: 13, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 12 }}>{heroDisplay.desc}</div>}
          <button onClick={openVoiceEditor} style={{ background: "transparent", color: G, border: `1px solid ${G}`, borderRadius: 6, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            Change voice
          </button>
        </div>
      )}

      {/* Expanded editor state */}
      {voiceEditing && (
        <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 10, padding: "20px", marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 20, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            Pick a writing style or a personality type.
          </div>

          {/* Writing Styles */}
          <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 11, letterSpacing: "0.15em", color: "#999", textTransform: "uppercase", marginBottom: 10 }}>Writing Styles</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
            {TONES.map(t => {
              const isSelected = !draftMbti && draftTone === t.id;
              return (
                <button key={t.id} onClick={() => selectDraftTone(t.id)}
                  style={{ background: isSelected ? "#EBF5FB" : "transparent", color: isSelected ? G : "#666", border: `1px solid ${isSelected ? G : B}`, borderRadius: 20, padding: "6px 16px", fontSize: 13, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontWeight: isSelected ? 600 : 400 }}>
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Personality Types */}
          <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 11, letterSpacing: "0.15em", color: "#999", textTransform: "uppercase", marginBottom: 10 }}>Personality Types</div>
          {MBTI_GROUPS.map(group => (
            <div key={group.id} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                {group.icon} {group.name} <span style={{ fontWeight: 400, color: "#888" }}>— {group.sub}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 6 }}>
                {group.types.map(t => {
                  const isSelected = draftMbti === t.type;
                  return (
                    <button key={t.type} onClick={() => selectDraftMbti(t.type)}
                      style={{
                        padding: "8px 10px", borderRadius: 8, cursor: "pointer", textAlign: "left",
                        border: `1.5px solid ${isSelected ? G : B}`,
                        background: isSelected ? "#EBF5FB" : "transparent",
                        fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
                      }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? G : "#333" }}>{t.type}</div>
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{t.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Sample preview */}
          <div style={{ background: "#f8f7f5", border: `1px solid ${B}`, borderRadius: 10, padding: "16px 18px", marginTop: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
              Sample — {previewSample.label} voice
            </div>
            {previewSample.desc && <div style={{ fontSize: 12, color: "#555", marginBottom: 8, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{previewSample.desc}</div>}
            <div style={{ fontSize: 14, color: "#333", lineHeight: 1.6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontStyle: "italic" }}>
              "{previewSample.sample}"
            </div>
          </div>

          {/* Cancel + Save buttons */}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={cancelVoiceEditor} style={{ background: "transparent", color: "#666", border: `1px solid ${B}`, borderRadius: 6, padding: "10px 20px", fontSize: 13, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
              Cancel
            </button>
            <button onClick={saveVoice} disabled={voiceSaving} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", opacity: voiceSaving ? 0.6 : 1 }}>
              {voiceSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Topics section moved to Content Sources */}
    </div>
  );
}
